import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { View } from '@actual-app/components/view';
import {
  suggestIncomeDistribution,
  validateIncomeDistribution,
} from '@actual-app/finance-engine';
import type {
  IncomeDistributionAllocation,
  IncomeDistributionRule,
  IncomeDistributionSource,
} from '@actual-app/finance-engine';

import { FinancialText } from '#components/FinancialText';

import type { ReadyFinancialDashboard } from './client';
import { formatMoneyInput, formatProductMoney, parseMoneyInput } from './money';

export type IncomeDistributionContext = Readonly<{
  rules: readonly IncomeDistributionRule[];
  goals: ReadyFinancialDashboard['financialGoals'];
  currency: string;
}>;

export type IncomeDistributionSelection =
  | readonly IncomeDistributionAllocation[]
  | undefined
  | null;

export function IncomeDistributionEditor({
  context,
  source,
  amountMinorUnits,
  isDisabled,
  onChange,
}: {
  context: IncomeDistributionContext;
  source: IncomeDistributionSource;
  amountMinorUnits: number;
  isDisabled: boolean;
  onChange: (selection: IncomeDistributionSelection) => void;
}) {
  const { t } = useTranslation();
  const goals = context.goals.filter(
    summary => summary.remainingAmount.minorUnits > 0,
  );
  const capacities = goals.map(summary => ({
    id: summary.goal.id,
    remainingMinorUnits: summary.remainingAmount.minorUnits,
  }));
  const suggestions = suggestIncomeDistribution({
    rules: context.rules,
    source,
    amountMinorUnits,
    currency: context.currency,
    goals: capacities,
  });
  const [amounts, setAmounts] = useState<Readonly<Record<string, string>>>(() =>
    Object.fromEntries(
      goals.map(summary => [
        summary.goal.id,
        formatMoneyInput({
          minorUnits:
            suggestions.find(item => item.goalId === summary.goal.id)
              ?.amountMinorUnits ?? 0,
          currency: context.currency,
        }),
      ]),
    ),
  );
  const [isConfirmed, setIsConfirmed] = useState(false);

  function readSelection(values: Readonly<Record<string, string>>) {
    try {
      const allocations: IncomeDistributionAllocation[] = [];
      for (const summary of goals) {
        const value = values[summary.goal.id] ?? '0';
        if (/^0+(?:[.,]0{0,2})?$/.test(value.trim())) continue;
        const amount = parseMoneyInput(value);
        if (amount === null) return null;
        allocations.push({ goalId: summary.goal.id, amountMinorUnits: amount });
      }
      const available = validateIncomeDistribution(
        amountMinorUnits,
        allocations,
        capacities,
      );
      return { allocations, available };
    } catch {
      return null;
    }
  }

  function publish(
    values: Readonly<Record<string, string>>,
    confirmed: boolean,
  ) {
    const selection = readSelection(values);
    onChange(confirmed ? (selection?.allocations ?? null) : undefined);
  }

  const preview = readSelection(amounts);
  if (goals.length === 0) return null;

  return (
    <fieldset
      disabled={isDisabled}
      style={{ margin: '16px 0', padding: 12, minWidth: 0 }}
    >
      <legend>
        <Trans>Suggested income distribution</Trans>
      </legend>
      <View style={{ gap: 10 }}>
        <p style={{ margin: 0 }}>
          <Trans>
            Each percentage uses the full received income. Suggestions stop at
            the goal target.
          </Trans>
        </p>
        <p style={{ margin: 0 }}>
          <Trans>
            Edit the suggested amounts or enter zero to omit a goal.
          </Trans>
        </p>
        {goals.map(summary => (
          <label
            key={summary.goal.id}
            htmlFor={`income-saving-${summary.goal.id}`}
          >
            {summary.goal.name}
            <Input
              id={`income-saving-${summary.goal.id}`}
              aria-label={t('Amount to set aside for {{name}}', {
                name: summary.goal.name,
              })}
              inputMode="decimal"
              value={amounts[summary.goal.id] ?? '0'}
              onChangeValue={value => {
                const next = { ...amounts, [summary.goal.id]: value };
                setAmounts(next);
                publish(next, isConfirmed);
              }}
            />
          </label>
        ))}
        <output aria-live="polite" style={{ display: 'grid', gap: 6 }}>
          {preview !== null && (
            <>
              <span>
                <Trans>Suggested savings</Trans>:{' '}
                <FinancialText>
                  {formatProductMoney({
                    minorUnits: amountMinorUnits - preview.available,
                    currency: context.currency,
                  })}
                </FinancialText>
              </span>
              <span>
                <Trans>Income remaining after savings</Trans>:{' '}
                <FinancialText>
                  {formatProductMoney({
                    minorUnits: preview.available,
                    currency: context.currency,
                  })}
                </FinancialText>
              </span>
            </>
          )}
        </output>
        {preview === null && (
          <p role="alert">
            <Trans>
              Distribution must fit within this income and each goal's remaining
              target.
            </Trans>
          </p>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={isConfirmed}
            onChange={event => {
              setIsConfirmed(event.target.checked);
              publish(amounts, event.target.checked);
            }}
          />
          <Trans>Confirm these savings contributions with this income</Trans>
        </label>
        <Button
          type="button"
          onPress={() => {
            setAmounts(
              Object.fromEntries(goals.map(summary => [summary.goal.id, '0'])),
            );
            setIsConfirmed(false);
            onChange(undefined);
          }}
        >
          <Trans>Skip distribution</Trans>
        </Button>
      </View>
    </fieldset>
  );
}
